import React from 'react'
import { HelpCircle } from 'react-feather'
import styled from 'styled-components'

// Import static token logos
import ataLogo from '../../assets/images/tokens/ata.png'
import busdLogo from '../../assets/images/tokens/busd.png'
import daiLogo from '../../assets/images/tokens/dai.png'
import usdcLogo from '../../assets/images/tokens/usdc.png'
import usdtLogo from '../../assets/images/tokens/usdt.png'
import wethLogo from '../../assets/images/tokens/weth.png'
import wmaticLogo from '../../assets/images/tokens/wmatic.png'

const Wrapper = styled.div<{ size: number }>`
  position: relative;
  display: flex;
  flex-direction: row;
  width: ${({ size }) => size * 1.5}px;
  height: ${({ size }) => size}px;
`

const TokenLogo = styled.img<{ size: number }>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  border-radius: 50%;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  background-color: white;
`

const HigherLogo = styled(TokenLogo)`
  position: relative;
  z-index: 2;
`

const CoveredLogo = styled(TokenLogo)`
  position: absolute;
  left: ${({ size }) => size / 2}px;
  z-index: 1;
`

const FallbackIcon = styled(HelpCircle)<{ size: number }>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.bg3};
  color: ${({ theme }) => theme.text3};
  padding: 2px;
`

const HigherFallback = styled(FallbackIcon)`
  position: relative;
  z-index: 2;
`

const CoveredFallback = styled(FallbackIcon)`
  position: absolute;
  left: ${({ size }) => size / 2}px;
  z-index: 1;
`

// Token address to logo mapping (case-insensitive)
const TOKEN_LOGOS: { [address: string]: string } = {
  // ATA - all chains
  '0xa2120b9e674d3fc3875f415a7df52e382f141225': ataLogo, // BSC ATA
  '0x0df0f72ee0e5c9b7ca761ecec42754992b2da5bf': ataLogo, // Polygon ATA

  // USDT - all chains
  '0x55d398326f99059ff775485246999027b3197955': usdtLogo, // BSC USDT
  '0xc2132d05d31c914a87c6611c10748aeb04b58e8f': usdtLogo, // Polygon USDT
  '0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9': usdtLogo, // Arbitrum USDT

  // USDC - all chains
  '0x2791bca1f2de4661ed88a30c99a7a9449aa84174': usdcLogo, // Polygon USDC
  '0xff970a61a04b1ca14834a43f5de4533ebddb5cc8': usdcLogo, // Arbitrum USDC
  '0x625e7708f30ca75bfd92586e17077590c60eb4cd': usdcLogo, // Polygon aPolUSDC -> use USDC

  // BUSD
  '0xe9e7cea3dedca5984780bafc599bd69add087d56': busdLogo, // BSC BUSD

  // DAI
  '0x8f3cf7ad23cd3cadbd9735aff958023239c6a063': daiLogo, // Polygon DAI

  // WETH
  '0x82af49447d8a07e3bd95bd0d56f35241523fbab1': wethLogo, // Arbitrum WETH
  '0x7ceb23fd6bc0add59e62ac25578270cff1b9f619': wethLogo, // Polygon WETH

  // WMATIC/WPOL
  '0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270': wmaticLogo // Polygon WMATIC
}

function getTokenLogo(address: string): string | null {
  return TOKEN_LOGOS[address.toLowerCase()] || null
}

interface TokenLogoImageProps {
  address: string
  size: number
  isHigher?: boolean
}

function TokenLogoImage({ address, size, isHigher }: TokenLogoImageProps) {
  const logoSrc = getTokenLogo(address)

  const LogoComponent = isHigher ? HigherLogo : CoveredLogo
  const FallbackComponent = isHigher ? HigherFallback : CoveredFallback

  if (!logoSrc) {
    return <FallbackComponent size={size} />
  }

  return <LogoComponent src={logoSrc} size={size} alt="" />
}

interface PairTokenLogosProps {
  token0Address: string
  token1Address: string
  size?: number
}

export default function PairTokenLogos({
  token0Address,
  token1Address,
  size = 24
}: PairTokenLogosProps) {
  return (
    <Wrapper size={size}>
      <TokenLogoImage address={token0Address} size={size} isHigher />
      <TokenLogoImage address={token1Address} size={size} />
    </Wrapper>
  )
}
